// @flow
import useHighlightLine from 'esm-web-pro/hooks/useHighlightLine';
import { Box } from 'grommet';
import * as React from 'react';
import type { LeafletContext } from 'react-leaflet/lib/types';
import { Route, withRouter } from 'react-router-dom';
import { withLeaflet } from 'react-leaflet';
import HorizontalLine from 'esm-web-pro/components/layout/HorizontalLine';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import { FormattedMessage } from 'react-intl';
import UpperContainer from 'esm-web-pro/components/layout/sidePanel/UpperContainer';
import PipeIcon from 'esm-web-pro/components/layout/sidePanel/icons/PipeIcon';
import SidePane from 'esm-web-pro/components/layout/SidePane';
import ModelAttributes from 'esm-web-pro/containers/pages/network/ModelAttributes';
import UseHighlightPoint from 'esm-web-pro/hooks/useHighlightPoint';
import type { LoadingCallback } from 'esm-web-pro/containers/map/editor/BaseEditor';
import NavigateBackButton from 'esm-web-pro/components/maps/NavigateBackButton';
import BottomContainer from 'esm-web-pro/components/layout/sidePanel/BottomContainer';
import PipeAttributes from './PipeAttributes';
import PipeContent from './PipeContent';
import PipeActions from './PipeActions';

type Props = {
    panelId: String,
    object: Pipe,
    leaflet: LeafletContext,
    map: { is_draft: boolean, uuid: string },
    history: Object,
    match: Object,
    updateObject: Function,
    editor: BaseEditor,
}

export const numberPointsCallbacksPipe = (pipe: Object): LoadingCallback[] => {
    const {
        start_termination_point: startTerminationPoint, end_termination_point: endTerminationPoint,
        start_container_point: startContainerPoint, end_container_point: endContainerPoint,
    } = pipe;

    const startPointCoords = startContainerPoint.coordinates.coordinates;
    const endPointCoords = endContainerPoint.coordinates.coordinates;

    const startPoint = startTerminationPoint || startContainerPoint;
    const endPoint = endTerminationPoint || endContainerPoint;

    let startNumber = startPoint.sequence;
    if (startPoint.type === 'pop' && startPoint.alias) {
        startNumber = startPoint.alias;
    }
    let endNumber = endPoint.sequence;
    if (endPoint.type === 'pop' && endPoint.alias) {
        endNumber = endPoint.alias;
    }

    return [
        UseHighlightPoint(startPointCoords, startNumber),
        UseHighlightPoint(endPointCoords, endNumber),
    ];
};

const PipeComponent = (props: Props) => {
    const {
        object: pipe, leaflet: { map }, map: networkMap, updateObject,
        editor, match, panelId,
    } = props;
    const { gis_line: { path: gisPath } } = pipe;

    const [clicked, setClicked] = React.useState(null);

    const pipeCoords = gisPath.coordinates.map((c) => [c[1], c[0]]);

    const highlightPipe = useHighlightLine(pipeCoords, [
        {
            color: pipe.color, dashArray: '20', className: 'route-line',
        },
        {
            color: pipe.color, dashArray: null, className: '',
        }],
    null);

    const numberPoints = numberPointsCallbacksPipe(pipe);

    const handleView = () => {
        editor.unregisterAllLoadingCallback();
        editor.registerMultipleLoadingCallbacks(numberPoints);
        editor.registerLoadingCallback(highlightPipe);
        if (map) {
            map.highlightedGeometry = {
                bounds: pipeCoords, center: null,
            };
            map.panInsideBounds(pipeCoords);
        }
        editor.reloadFeatures();
    };

    React.useEffect(() => {
        handleView();
        return () => {
            editor.unregisterAllLoadingCallback();
        };
    }, [pipe.color]);

    const navigateBack = () => {
        props.history.push(props.match.url.split('/layer')[0]);
    };

    const handleEdit = (data: Object) => {
        updateObject(data);
    };

    return (
        <SidePane
            id={panelId}
            openInitially
            closeAllOpened
            title={pipe.sequence}
            titleLabel={<FormattedMessage id="pipe-panel-title" defaultMessage="Pipe" />}
            titleIcon={<PipeIcon />}
            titleOnView={handleView}
            actions={<NavigateBackButton panelId={panelId} />}
        >
            <Box height="100%" justify="between">
                <Box flex>
                    <UpperContainer>
                        <HorizontalLine color="grey" />
                        <PipeAttributes
                            pipe={pipe}
                            url={props.match.url}
                            onView={handleView}
                        />
                        <HorizontalLine color="grey" />
                        <Box direction="row">
                            <PipeActions
                                pipe={pipe}
                                map={networkMap}
                                onEdit={handleEdit}
                                navigateBack={navigateBack}
                                onView={handleView}
                                editor={editor}
                            />
                        </Box>
                        <HorizontalLine color="grey" />
                    </UpperContainer>
                    <BottomContainer>
                        <PipeContent
                            pipe={pipe}
                            editor={editor}
                            clicked={clicked}
                            setClicked={setClicked}
                            map={map}
                        />
                    </BottomContainer>
                </Box>

                <Route
                    path={`${match.path}/model/:modelCode`.replace('//', '/')}
                    render={({ match: _match }) => (
                        <ModelAttributes url={_match.url} />
                    )}
                />
            </Box>
        </SidePane>
    );
};

export default withRouter(withLeaflet(PipeComponent));
